import { DecimalPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  contentChild,
  ElementRef,
  inject,
  input,
  NO_ERRORS_SCHEMA,
  OnInit,
  Renderer2,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';

import { distinctUntilChanged, takeUntil, tap } from 'rxjs';

import { ErrorMessageComponent, FormInput, GovukValidators, LabelDirective } from '@netz/govuk-components';

import { LabelSizeType } from './label-size.type';
import { GovukTextWidthClass, HTMLInputType } from './text-input.type';

/*
 eslint-disable
 @angular-eslint/component-selector
 */
@Component({
  selector: 'div[cca-text-input]',
  templateUrl: './text-input.component.html',
  imports: [ErrorMessageComponent, NgClass, NgTemplateOutlet],
  providers: [DecimalPipe],
  schemas: [NO_ERRORS_SCHEMA],
})
export class TextInputComponent extends FormInput implements ControlValueAccessor, OnInit, AfterViewInit {
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly renderer = inject(Renderer2);

  protected readonly hint = input<string>(undefined);
  protected readonly inputType = input<HTMLInputType>('text');
  protected readonly autoComplete = input('on');
  protected readonly inputMode = input<string>(undefined);
  protected readonly spellCheck = input<boolean>(undefined);
  protected readonly numberFormat = input<string>(undefined);
  protected readonly widthClass = input<GovukTextWidthClass>('govuk-!-width-full');
  protected readonly prefix = input<string>(undefined);
  protected readonly suffix = input<string>(undefined);
  protected readonly valueTransform = input<(v: unknown) => string>(undefined);
  protected readonly label = input<string>();
  protected readonly labelSize = input<LabelSizeType>();

  protected readonly templateLabel = contentChild(LabelDirective);
  protected readonly input = viewChild<ElementRef<HTMLInputElement>>('input');

  protected readonly currentLabel = computed(() => this.label() || 'Insert label');
  protected readonly isLabelHidden = computed(() => (this.label() ? false : true));

  protected readonly currentLabelSize = computed(() => {
    switch (this.labelSize()) {
      case 'small':
        return 'govuk-label govuk-label--s';
      case 'medium':
        return 'govuk-label govuk-label--m';
      case 'large':
        return 'govuk-label govuk-label--l';
      default:
        return 'govuk-label';
    }
  });

  disabled: boolean;
  onChange: (_: unknown) => void;
  onBlur: (_: unknown) => void;

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.inputType() === 'number') {
      const notNanValidator = GovukValidators.notNaN('Enter a numerical value, without alpha or special characters');
      this.control.addValidators(notNanValidator);
      this.control.updateValueAndValidity();
    }
  }

  ngAfterViewInit(): void {
    this.writeValue(this.control.value);
    this.control.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => {
          const previousValue = this.inputType() === 'number' ? Number(prev) : prev;
          const currentValue = this.inputType() === 'number' ? Number(curr) : curr;
          return previousValue === currentValue;
        }),
        tap((value) => this.handleInputValue(value)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  writeValue(value: unknown): void {
    const inputValue = this.input();
    if (inputValue) {
      const numberFormat = this.numberFormat();
      const displayValue =
        inputValue.nativeElement === document.activeElement
          ? value
          : numberFormat && typeof value !== 'object'
            ? this.decimalPipe.transform(value as string | number, numberFormat)
            : value;

      this.renderer.setProperty(inputValue.nativeElement, 'value', displayValue);
    }
  }

  registerOnChange(onChange: (value: unknown) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onBlur: () => void): void {
    this.onBlur = onBlur;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  onFocus(): void {
    switch (this.inputType()) {
      case 'number':
        if (this.numberFormat()) {
          this.renderer.setProperty(this.input().nativeElement, 'value', this.control.value);
        }
        break;
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.handleBlur(this.getInputValue(event));
    }
  }

  handleBlur(value?: string): void {
    this.onBlur(value);
  }

  private handleInputValue(value: string) {
    switch (this.inputType()) {
      case 'number':
        if (value === null) {
          break;
        } else if (value === '') {
          this.control.setValue(null);
        } else if (!isNaN(Number(value))) {
          this.control.setValue(Number(value));

          const inputValue = this.input();
          if (inputValue.nativeElement !== document.activeElement) {
            const numberFormat = this.numberFormat();
            this.renderer.setProperty(
              inputValue.nativeElement,
              'value',
              numberFormat ? this.decimalPipe.transform(value, numberFormat) : value,
            );
          }
        }
        break;
      case 'text':
        this.control.setValue(value ? (value.trim() === '' ? null : value.trim()) : value, {
          emitEvent: false,
          emitViewToModelChange: false,
          emitModelToViewChange: false,
        });
    }
  }
}
